import type { TridentCampaignDetails } from 'types/trident-campaigns';
import type { Country } from 'types/country';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import i18n from 'i18n-js';
import _ from 'lodash';
import moment from 'moment';
import CDatePicker from 'components/custom/CDatePicker';
import CountrySelect from 'components/form/CountrySelect';
import FormInputWithError from 'components/form/FormInputWithError';
import StatusLabel from 'components/custom/StatusLabel';
import { Grid, Form, Label, Icon } from 'semantic-grab-ui/src';
import { overviewConstraints } from './constraints';
import OffersDiscoveryDetails from './OffersDiscoveryDetails.tsx';
import formComponent from 'components/decorators/form_component';
import {
  nodesOccMapData,
  targetTypeList,
  ownerTypeList,
  defaultOwner,
  userTypeToTemplateAppName,
  tridentUserVsUCDUser,
} from 'data/trident-campaigns';
import { getTimezoneOptions } from 'utils/time-utils';
import { nodeTypes } from 'data/trident-types';
import { getAllowedTargetUserTypesList } from 'omega-rtc/utils/user-utils';
import { getUCDCampaign } from 'actions/ucd';
import ProjectSearch from 'rtc-widget';
import { getApiBaseUrl } from '../../../config';
import './OverviewForm.scss';
import MetadataForm from 'omega-rtc/components/form/trident-campaigns/MetadataForm';
import BudgetForm from 'omega-rtc/components/form/trident-campaigns/BudgetForm';
import GMSEnabledDetails from './GMSEnabledDetails';
import { getCVPOptionsBasedOnVerticalsAndCountry } from 'omega-rtc/utils/smart-campaign-utils';
import {
  getContentPortalProductOfferingOptions,
  getNextTargetVerticalCVP,
  isContentPortalProductsEnabled,
} from './content-portal-product-offerings';

type Props = {
  model: TridentCampaignDetails;
  country?: Country;
  canActivate: boolean;
  onModelChange: (_?: any) => void;
  hasError: (field: string) => boolean;
  handleInputChange: (event: any, data: any) => void;
  handleControlBlur: (event: any, data: any) => void;
  handleOnCountryChange: (country: Country) => void;
  error: object;
  userPermissions: object;
  allowedOwners: any[];
  campaignObjectives: any[];
  campaignBudgetSources: any[];
  campaignTargetVerticals: any[];
  awardPointAction: object;
  featureFlagConfig: object;
  contentPortalProducts: any[];
  hasContentPortalProductsLoaded: boolean;
  getUCDCampaign: (ucdProjectID: string, ucdCampaignID: string) => void;
  ucdCampaignView: object;
  routerProps: any;
};

@formComponent(overviewConstraints)
class OverviewForm extends Component<Props> {
  ucdCampaignID = '';

  componentDidMount() {
    const searchParams = new URLSearchParams(this.props.routerProps.location.search);
    const ucdProjectID = searchParams.get('ucdProjectID');
    const ucdCampaignID = searchParams.get('ucdCampaignID');
    if (ucdProjectID && ucdCampaignID && !this.isEdit()) {
      this.ucdCampaignID = ucdCampaignID;
      this.props.getUCDCampaign(ucdProjectID, ucdCampaignID);
    }


    const { model } = this.props;
    const selectedVertical = _.get(model, 'targetVertical');
    const selectedCVP = _.get(model, 'targetVerticalCVP');

    if (isContentPortalProductsEnabled()) {
      this.syncContentPortalProductOffering(this.props);
      return;
    }

    // This is to handle scenario when user editing or duplicating old campaign where targetVertical is selected but targetVerticalCVP is not yet selected
    if (!_.isEmpty(selectedVertical) && _.isEmpty(selectedCVP)) {
      const updatedModel = _.cloneDeep(model);
      _.set(updatedModel, 'targetVerticalCVP', 'Others');
      this.props.onModelChange(updatedModel);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    // update objectiveList and objectiveDescriptions with latest props
    const objectives = _.get(nextProps, 'campaignObjectives', []);
    const descriptions = {};
    const list = [];
    if (!_.isEmpty(objectives)) {
      objectives.forEach(
        o => {
          _.set(descriptions, o.value, o.description);
          list.push({
            key: o.value, value: o.value, text: o.value,
          });
        }
      );
    }

    if (nextProps.ucdCampaignView !== this.props.ucdCampaignView) {
      const ucdCampaignView = nextProps.ucdCampaignView;
      const model = this.props.model;
      _.set(model, 'name', _.get(ucdCampaignView, 'campaign.code'));
      _.set(model, 'countryID', _.get(ucdCampaignView, 'countryID'));
      _.set(model, 'ucdProjectID', _.get(ucdCampaignView, 'projectID'));
      _.set(model, 'budgetSource', _.get(ucdCampaignView, 'campaign.budgetSource'));
      _.set(model, 'targetVertical', _.get(ucdCampaignView, 'campaign.verticals[0]'));
      _.set(model, 'createdAt', _.get(ucdCampaignView, 'campaign.createdAt'));
      const ucdUser = _.get(ucdCampaignView, 'audience');
      const tridentUser = ucdUser ? Object.keys(tridentUserVsUCDUser).find(key => tridentUserVsUCDUser[key] === ucdUser) : null;
      _.set(model, 'targetUserType', tridentUser);
      const startTime = moment(_.get(ucdCampaignView, 'campaign.start'));
      const endTime = moment(_.get(ucdCampaignView, 'campaign.end'));
      if (startTime.isValid() && endTime.isValid()) {
        _.set(model, 'startTime', startTime);
        _.set(model, 'endTime', endTime);
      }
      const updatedModel = _.cloneDeep(model);
      _.set(updatedModel, 'ucdCampaignID', this.ucdCampaignID);
      this.props.onModelChange(updatedModel);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!isContentPortalProductsEnabled()) {
      return;
    }

    const didProductsChange =
      prevProps.contentPortalProducts !== this.props.contentPortalProducts ||
      prevProps.hasContentPortalProductsLoaded !== this.props.hasContentPortalProductsLoaded;
    const didVerticalChange = _.get(prevProps.model, 'targetVertical') !== _.get(this.props.model, 'targetVertical');
    const didCvpChange = _.get(prevProps.model, 'targetVerticalCVP') !== _.get(this.props.model, 'targetVerticalCVP');

    if (didProductsChange || didVerticalChange || didCvpChange) {
      this.syncContentPortalProductOffering(this.props);
    }
  }

  handleDateChange = (event: any, data: any) => {
    let updatedModel = _.cloneDeep(this.props.model);
    updatedModel = { ...updatedModel, [data.name]: data.value };
    _.map(this.props.model.nodes, (node, nodeIndex) => {
      if (node.type === nodeTypes.scenarios) {
        const isSameDateChecked = _.get(node, 'data.meta.isSameDateChecked', false);
        if (isSameDateChecked) {
          _.set(updatedModel, `nodes[${nodeIndex}].data.meta.${data.name}`, data.value);
        }
      }
      if (node.type === nodeTypes.schedule) {
        const isSameDateChecked = _.get(node, 'data.isSameDateChecked', false);
        if (isSameDateChecked) {
          _.set(updatedModel, `nodes[${nodeIndex}].data.${data.name}`, data.value);
          // sync next trigger time if the schedule is not triggered yet, and start time is being changed
          if (_.isEmpty(_.get(updatedModel, `nodes[${nodeIndex}].data.lastHappenedAt`)) && data.name === 'startTime') {
            _.set(updatedModel, `nodes[${nodeIndex}].data.nextTriggerTime`, data.value);
          }
        }
      }
    });
    this.props.onModelChange(updatedModel);
  };

  handleUserTypeChange = (event: any, data: any) => {
    this.handleChangeWithWarning('targetUserType', data.value, 'Changing user type will delete all existing nodes. Are you sure to change?');
  };

  handleOwnerChange = (event: any, data: any) => {
    this.handleChangeWithWarning('owner', data.value, 'Changing owner will delete all existing nodes. Are you sure to change?');
  };

  handleUCDProjectChange = (record: any) => {
    let updatedModel = _.cloneDeep(this.props.model);
    updatedModel = { ...updatedModel, ucdProjectID: _.get(record, 'projectID', ''), ucdProject: record };
    this.props.onModelChange(updatedModel);
  };

  // handleChangeWithWarning checks whether the value for `key` is changed, if yes, ask user to confirm the change, and clear nodes if confirmed
  handleChangeWithWarning = (key: string, newVal: any, warnMsg: string) => {
    const updatedModel = _.cloneDeep(this.props.model);
    const oldVal = _.get(updatedModel, key, '');
    if (_.isEmpty(oldVal)) {
      _.set(updatedModel, key, newVal);
      this.props.onModelChange(updatedModel);
    } else if (oldVal !== newVal) {
      // eslint-disable-next-line no-alert
      if (!confirm(warnMsg)) return;
      this.clearNodes(updatedModel);
      _.set(updatedModel, key, newVal);
      this.props.onModelChange(updatedModel);
    }
  };

  handleTimeZoneChange = (event: any, data: any) => {
    const timeZoneOptions = _.get(this.props, 'country.timezones');
    const timeZoneObject = timeZoneOptions.find(timeZone => timeZone.code === data.value);
    const timezoneName = _.get(timeZoneObject, 'tz');
    const updatedModel = _.cloneDeep(this.props.model);
    _.set(updatedModel, 'timezone', data.value);
    _.set(updatedModel, 'timezoneName', timezoneName);
    this.props.onModelChange(updatedModel);
  };

  handleCountryChange = (event: any, { country }: any) => {
    if (this.props.handleOnCountryChange) {
      this.props.handleOnCountryChange(country);
    }
  };

  clearNodes = (model: any) => {
    _.set(model, 'maxValueOfNodesKeyFromApi', 0);
    _.set(model, 'recordLastOccurrenceKey', 0);
    _.set(model, 'nodesOccMap', nodesOccMapData);
    _.set(model, 'nodes', {});
  };

  isEdit = (): boolean => {
    const edit = parseInt(_.get(this.props.model, 'maxValueOfNodesKeyFromApi', 0), 10) > 0;
    return edit;
  };

  isDuplicate = (): boolean => {
    return this.props.routerProps.location.search.includes('entityIDToDuplicate');
  };

  getArrayValue = (value: any) => {
    if (!Array.isArray(value)) return [];
    return value || [];
  };

  handleVerticalChange = (event: any, data: any) => {
    const updatedModel = _.cloneDeep(this.props.model);
    _.set(updatedModel, 'targetVertical', data.value);

    if (isContentPortalProductsEnabled()) {
      const cvpList = this.getCvpDropdownList(_.get(updatedModel, 'countryID'), data.value);
      _.set(updatedModel, 'targetVerticalCVP', getNextTargetVerticalCVP({
        currentValue: '',
        hasLoadedProducts: this.props.hasContentPortalProductsLoaded,
        options: cvpList,
        selectedVertical: data.value,
      }));
      this.props.onModelChange(updatedModel);
      return;
    }

    // Set default value for targetVerticalCVP when the Cvp list for the selected vertical is empty
    const cvpList = this.getCvpDropdownList(_.get(updatedModel, 'countryID'), data.value);
    if (_.isEmpty(cvpList)) {
      _.set(updatedModel, 'targetVerticalCVP', 'Others');
    } else {
      _.set(updatedModel, 'targetVerticalCVP', '');
    }
    this.props.onModelChange(updatedModel);
  };

  getCvpDropdownList = (countryID: number, vertical?: string, props: Props = this.props) => {
    if (isContentPortalProductsEnabled()) {
      return getContentPortalProductOfferingOptions({
        products: props.contentPortalProducts,
        vertical,
      });
    }

    const cvpList = getCVPOptionsBasedOnVerticalsAndCountry({
      countryID,
      vertical,
    }) || [];

    // map the dropdown list to the format required by trident Form.Select component
    return _.map(cvpList, cvp => ({
      key: cvp.label,
      text: cvp.label,
      value: cvp.key,
    }));
  };

  syncContentPortalProductOffering = (props: Props) => {
    const { model } = props;
    const selectedVertical = _.get(model, 'targetVertical', '');
    const currentValue = _.get(model, 'targetVerticalCVP', '');
    const cvpDropdownList = this.getCvpDropdownList(_.get(model, 'countryID'), selectedVertical, props);
    const nextValue = getNextTargetVerticalCVP({
      currentValue,
      hasLoadedProducts: props.hasContentPortalProductsLoaded,
      options: cvpDropdownList,
      selectedVertical,
    });

    if (nextValue === currentValue) {
      return;
    }

    const updatedModel = _.cloneDeep(model);
    _.set(updatedModel, 'targetVerticalCVP', nextValue);
    props.onModelChange(updatedModel);
  };

  render() {
    const { model, userPermissions, allowedOwners, ucdCampaignView } = this.props;
    const timezoneOptions = getTimezoneOptions(_.get(this.props, 'country.timezones'));
    const isEditPage = this.isEdit();
    const targetUserTypeList = isEditPage ? getAllowedTargetUserTypesList(userPermissions, targetTypeList, 'read') : getAllowedTargetUserTypesList(userPermissions, targetTypeList, 'create');
    const ownerSelectionList = ownerTypeList.filter(o => { return _.includes(allowedOwners, o.value); });
    const campaignMetadataFields = _.get(window.Grab.config, 'CAMPAIGN_METADATA_FIELDS');
    const isBudgetFeatureEnabled = _.get(window.Grab.config, 'BUDGET_FEATURE_ENABLED', false);

    const selectedVertical = _.get(model, 'targetVertical');
    const cvpDropdownList = this.getCvpDropdownList(model.countryID, selectedVertical);
    const showCvpDropdown = cvpDropdownList && cvpDropdownList.length > 0;

    return (
      <Grid>
        <Grid.Column width={2}>
          <Label
            size="large"
            color="teal"
          >
            <Icon name="setting" /> Overview
          </Label>
          <p />
          <StatusLabel status={model.status} />
        </Grid.Column>
        <Grid.Column width={14}>
          <Form>
            <Grid>
              <Grid.Column width={4}>
                <FormInputWithError
                  name="name"
                  {...this.props}
                >
                  <Form.Input
                    required
                    label={i18n.t('targets_page.fields.target_campaign_code')}
                    value={model.name}
                    onChange={this.props.handleInputChange}
                    onBlur={this.props.handleControlBlur}
                  />
                </FormInputWithError>
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInputWithError
                  name="countryID"
                  {...this.props}
                >
                  <CountrySelect
                    key={model.countryID}
                    required
                    name="countryID"
                    label={i18n.t('common.fields.country')}
                    value={model.countryID}
                    onChange={this.handleCountryChange}
                    onBlur={this.props.handleControlBlur}
                    error={this.props.hasError('countryID')}
                    disabled={isEditPage && !this.isDuplicate()}
                    disableConfirm
                  />
                </FormInputWithError>
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInputWithError
                  name="targetUserType"
                  {...this.props}
                >
                  <Form.Select
                    required
                    label={i18n.t('targets_page.fields.target_user_type')}
                    options={targetUserTypeList}
                    value={_.get(model, 'targetUserType')}
                    onChange={this.handleUserTypeChange}
                    onBlur={this.props.handleControlBlur}
                    disabled={isEditPage}
                  />
                </FormInputWithError>
              </Grid.Column>
            </Grid>
            <Grid>
              <Grid.Column width={4}>
                <FormInputWithError
                  name="startTime"
                  {...this.props}
                >
                  <CDatePicker
                    required
                    selectsStart
                    showTimeSelect
                    label={i18n.t('common.fields.startDate')}
                    selected={model.startTime}
                    startDate={model.startTime}
                    endDate={model.endTime}
                    handleInputChange={this.handleDateChange}
                    onBlur={this.props.handleControlBlur}
                  />
                </FormInputWithError>
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInputWithError
                  name="endTime"
                  {...this.props}
                >
                  <CDatePicker
                    required
                    showTimeSelect
                    label={i18n.t('common.fields.endDate')}
                    minDate={model.startTime}
                    selected={model.endTime}
                    startDate={model.startTime}
                    endDate={model.endTime}
                    handleInputChange={this.handleDateChange}
                    onBlur={this.props.handleControlBlur}
                  />
                </FormInputWithError>
              </Grid.Column>
              <Grid.Column width={4}>
                <FormInputWithError
                  name="timezone"
                  {...this.props}
                >
                  <Form.Select
                    label={i18n.t('rewards_page.fields.timezone')}
                    options={timezoneOptions}
                    value={model.timezone}
                    onChange={this.handleTimeZoneChange}
                  />
                </FormInputWithError>
              </Grid.Column>
            </Grid>
            <Grid>
              <Grid.Column width={4}>
                <FormInputWithError
                  name="owner"
                  {...this.props}
                >
                  {
                    !isEditPage
                      ? <Form.Select
                        label="Owner"
                        options={ownerSelectionList}
                        value={_.get(model, 'owner')}
                        onChange={this.handleOwnerChange}
                        onBlur={this.props.handleControlBlur}
                      />
                      : <Form.Input
                        disabled
                        label="Owner"
                        value={_.get(model, 'owner')}
                      />
                  }
                </FormInputWithError>
              </Grid.Column>
              {_.get(model, 'owner') !== defaultOwner.value &&
                <Grid.Column width={4}>
                  <FormInputWithError
                    name="ownerReferenceID"
                    {...this.props}
                  >
                    <Form.Input
                      required
                      label="Owner Reference ID"
                      value={_.get(model, 'ownerReferenceID')}
                      onChange={this.props.handleInputChange}
                      onBlur={this.props.handleControlBlur}
                    />
                  </FormInputWithError>
                </Grid.Column>
              }
            </Grid>
            {
              _.get(this.props.featureFlagConfig, 'ucdFlowEnabled', false) &&
              <Grid>
                <Grid.Column width={4}>
                  <FormInputWithError
                    label="Holos Project"
                    name="holosProject"
                    {...this.props}
                  >
                    <div className="field">
                      <label>Holos Project</label>
                      <ProjectSearch
                        disabled={!(model.countryID && model.targetUserType) || ucdCampaignView}
                        apiBaseURL={getApiBaseUrl()}
                        countryID={`${model.countryID}`}
                        audience={userTypeToTemplateAppName[model.targetUserType]}
                        value={model.ucdProjectID}
                        callback={this.handleUCDProjectChange}
                      />
                    </div>
                  </FormInputWithError>
                </Grid.Column>
                <Grid.Column width={4}>
                  <FormInputWithError name="budgetSource" {...this.props}>
                    <Form.Select
                      required={model.ucdProjectID}
                      disabled={!model.ucdProjectID || ucdCampaignView}
                      label="Budget Source"
                      options={this.getArrayValue(this.props.campaignBudgetSources)}
                      value={_.get(model, 'budgetSource')}
                      onChange={this.props.handleInputChange}
                      onBlur={this.props.handleControlBlur}
                    />
                  </FormInputWithError>
                </Grid.Column>
                <Grid.Column width={4}>
                  <FormInputWithError name="targetVertical" {...this.props}>
                    <Form.Select
                      required={model.ucdProjectID}
                      disabled={!model.ucdProjectID || ucdCampaignView}
                      label="Target Vertical"
                      options={this.getArrayValue(this.props.campaignTargetVerticals)}
                      value={selectedVertical}
                      onChange={this.handleVerticalChange}
                      onBlur={this.props.handleControlBlur}
                      placeholder="Select Vertical"
                    />
                  </FormInputWithError>
                </Grid.Column>

                {showCvpDropdown && (
                  <Grid.Column width={4}>
                    <FormInputWithError name="targetVerticalCVP" {...this.props}>
                      <Form.Select
                        required
                        label="Product Offering"
                        options={cvpDropdownList}
                        value={_.get(model, 'targetVerticalCVP')}
                        onChange={this.props.handleInputChange}
                        onBlur={this.props.handleControlBlur}
                        name="targetVerticalCVP"
                        placeholder="Select CVP"
                      />
                    </FormInputWithError>
                  </Grid.Column>
                )}
              </Grid>
            }
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <OffersDiscoveryDetails
                model={model}
                onModelChange={this.props.onModelChange}
                handleInputChange={this.props.handleInputChange}
                handleControlBlur={this.props.handleControlBlur}
                error={this.props.error}
                hasError={this.props.hasError}
              />
              <GMSEnabledDetails
                model={model}
                onModelChange={this.props.onModelChange}
                handleInputChange={this.props.handleInputChange}
                handleControlBlur={this.props.handleControlBlur}
                error={this.props.error}
                hasError={this.props.hasError}
              />
            </div>
            {isBudgetFeatureEnabled && <BudgetForm
              model={model}
              handleInputChange={this.props.handleInputChange}
              handleControlBlur={this.props.handleControlBlur}
            />
            }
            {campaignMetadataFields && <MetadataForm
              model={model}
              handleInputChange={this.props.handleInputChange}
              handleControlBlur={this.props.handleControlBlur}
              inputFields={campaignMetadataFields}
            />
            }
          </Form>
        </Grid.Column>
      </Grid>
    );
  }
}

export function mapStateToProps(state: any) {
  return {
    routerProps: { location },
    ucdCampaignView: _.get(state.ucd, 'campaignView'),
  };
}

export default connect(mapStateToProps, {
  getUCDCampaign,
})(OverviewForm);
